import {
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Blocker as NavigationBlocker,
  BlockerFunction,
  useBlocker,
} from 'react-router-dom';

import { UnsavedChangesConfirmationModal } from '@/components/modals/ConfirmationModal/UnsavedChangesConfirmationModal';
import { TROWSER_PARAM } from '@/modules/navigation/trowsers/trowser.types';

import { UnloadPromptContext } from './unloadPrompt.context';

// Global flag to track when we're in the onbeforeunload phase
export let isInBeforeUnloadPhase = false;

export const CONFIRMATION_MESSAGE = 'You have unsaved changes. Continue?';

function ConfirmNavigation({
  blocker,
  setShouldBlockNavCtx,
}: {
  blocker: NavigationBlocker;
  setShouldBlockNavCtx: UnloadPromptContext['setShouldBlockNavCtx'];
}) {
  if (blocker.state === 'blocked') {
    return (
      <UnsavedChangesConfirmationModal
        isOpen={true}
        onClose={(reason) => {
          if (reason === 'confirm') {
            blocker.reset?.();
          } else {
            // Why setTimeout?
            // https://github.com/remix-run/react-router/blob/f9b3dbd9cbf513366c456b33d95227f42f36da63/packages/react-router-dom/index.tsx#L1475C16-L1475C16
            setTimeout(blocker.proceed, 0);
            setShouldBlockNavCtx(false);
          }
        }}
      />
    );
  }

  return null;
}

function Blocker({
  shouldBlockNavCtx,
  setShouldBlockNavCtx,
}: {
  shouldBlockNavCtx: boolean;
  setShouldBlockNavCtx: Dispatch<SetStateAction<boolean>>;
}) {
  const blockerFn = useCallback<BlockerFunction>(
    ({ currentLocation, nextLocation, historyAction }) => {
      if (!shouldBlockNavCtx) {
        return false;
      }

      if (currentLocation.pathname === nextLocation.pathname) {
        // Same-page navigations are normally silent. The exception is a browser
        // Back (POP) that closes an open trowser — the `modalOpen` param goes
        // away or shrinks — while the form is dirty. Prompt for unsaved changes
        // before letting the trowser close; confirming (Leave) lets the POP
        // proceed and the trowser closes.
        const current = new URLSearchParams(currentLocation.search).get(
          TROWSER_PARAM
        );
        const next = new URLSearchParams(nextLocation.search).get(
          TROWSER_PARAM
        );
        return historyAction === 'POP' && !!current && current !== next;
      }

      // If the user is navigating to a different page, we want to prompt them
      return true;
    },
    [shouldBlockNavCtx]
  );

  const blocker = useBlocker(blockerFn);

  return (
    <ConfirmNavigation
      blocker={blocker}
      setShouldBlockNavCtx={setShouldBlockNavCtx}
    />
  );
}

/**
 * @description This HOC is used to house a single instance of usePrompt, which as of July 2023
 * only supports a single instance of usePrompt per app. This is a workaround for that.
 *
 * `shouldBlockNavCtx` is a single boolean — but it's automatically
 * synced from a registry of subscribers (one per `useOnBeforeUnloadPrompt`
 * call). This way multiple forms on the same page can each report
 * their dirty state without racing: the boolean is true if ANY
 * registered subscriber is true. Imperative `setShouldBlockNavCtx`
 * writes directly to the boolean and continues to work as a
 * one-shot bypass before programmatic navigation.
 */
export function UnloadPromptProvider({ children }: PropsWithChildren<unknown>) {
  const [shouldBlockNavCtx, setShouldBlockNavCtx] =
    useState<UnloadPromptContext['shouldBlockNavCtx']>(false);
  const [subscribers, setSubscribers] = useState<Record<string, boolean>>({});
  const [isHandledByBrowser, setIsHandledByBrowser] = useState(false);

  const anySubscriberBlocks = useMemo(
    () => Object.values(subscribers).some(Boolean),
    [subscribers]
  );

  // Sync the subscriber-derived value into the canonical boolean.
  // Imperative `setShouldBlockNavCtx(false)` callers can still flip
  // the boolean off ahead of a programmatic navigation; the boolean
  // only resyncs when subscribers actually change.
  useEffect(() => {
    setShouldBlockNavCtx(anySubscriberBlocks);
  }, [anySubscriberBlocks]);

  const registerBlockSubscriber = useCallback(
    (id: string, shouldBlock: boolean) => {
      setSubscribers((prev) => {
        if (prev[id] === shouldBlock) return prev;
        return { ...prev, [id]: shouldBlock };
      });
    },
    []
  );

  const unregisterBlockSubscriber = useCallback((id: string) => {
    setSubscribers((prev) => {
      if (!(id in prev)) return prev;
      const { [id]: _removed, ...rest } = prev;
      return rest;
    });
  }, []);

  // this useEffect will prompt the user if they try to close the tab or reload the page
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      setIsHandledByBrowser(true);
      // Set the global flag to indicate we're in the beforeunload phase
      isInBeforeUnloadPhase = true;
      if (shouldBlockNavCtx) {
        event.preventDefault();
        event.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      setIsHandledByBrowser(false);
      isInBeforeUnloadPhase = false;
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [shouldBlockNavCtx]);

  const contextValue = useMemo(
    () => ({
      shouldBlockNavCtx,
      setShouldBlockNavCtx,
      registerBlockSubscriber,
      unregisterBlockSubscriber,
    }),
    [
      shouldBlockNavCtx,
      setShouldBlockNavCtx,
      registerBlockSubscriber,
      unregisterBlockSubscriber,
    ]
  );

  return (
    <UnloadPromptContext.Provider value={contextValue}>
      {!isHandledByBrowser && (
        <Blocker
          shouldBlockNavCtx={shouldBlockNavCtx}
          setShouldBlockNavCtx={setShouldBlockNavCtx}
        />
      )}
      {children}
    </UnloadPromptContext.Provider>
  );
}
